import { formatDate } from '../../../../app/header/utils'
import { RedGreenText } from '../../../components/RedGreenText'
import { TableAlign } from '../../../components/Table'
import { formatNumber } from '../../../util'
import { ColumnId, RowKind, TableColumnDefinition } from '../types'
import { DATE_FORMAT_INT, DATE_FORMAT_VIEW, DATE_FORMAT_VIEW_LONG } from './common'

const NULL_VALUE = '-'
const FORMAT_SETTINGS = { defaultValue: NULL_VALUE }
const GREEK_SETTINGS = { defaultValue: NULL_VALUE, fractions: 4 }

export const columnMap: Record<ColumnId, TableColumnDefinition> = Object.freeze({
  [ColumnId.LastClose]: {
    id: ColumnId.LastClose,
    align: TableAlign.Right,
    name: 'Last Close',
    renderColumn: (args) => {
      if (args.kind === RowKind.OptionRow) return null
      return args.row.lastClose === 0 ? NULL_VALUE : formatNumber(args.row.lastClose, FORMAT_SETTINGS)
    },
  },
  [ColumnId.ChangeUsd]: {
    id: ColumnId.ChangeUsd,
    align: TableAlign.Right,
    name: 'Change $',
    getValueForSort: (args) => {
      if (args.kind === RowKind.OptionRow) return null
      if (args.row.lastClose === 0) return null
      return args.row.lastChange
    },
    renderColumn: (args) => {
      if (args.kind === RowKind.OptionRow) return null
      if (args.row.lastClose === 0) return NULL_VALUE

      return (
        <RedGreenText value={args.row.lastChange} rightContent={null}>
          {formatNumber(args.row.lastChange, { ...FORMAT_SETTINGS, showPlusSign: true })}
        </RedGreenText>
      )
    },
  },
  [ColumnId.ChangePct]: {
    id: ColumnId.ChangePct,
    align: TableAlign.Right,
    name: 'Change %',
    getValueForSort: (args) => {
      if (args.kind === RowKind.OptionRow) return null
      if (typeof args.row.lastClose !== 'number' || args.row.lastClose === 0) return null
      const prevClose = args.row.lastClose - args.row.lastChange
      return (args.row.lastClose / prevClose - 1) * 100
    },
    renderColumn: (args) => {
      if (args.kind === RowKind.OptionRow) return null
      if (typeof args.row.lastClose !== 'number' || args.row.lastClose === 0) return NULL_VALUE

      const prevClose = args.row.lastClose - args.row.lastChange
      const change = (args.row.lastClose / prevClose - 1) * 100

      return (
        <RedGreenText value={change} rightContent="%">
          {formatNumber(change, { ...FORMAT_SETTINGS, showPlusSign: true })}
        </RedGreenText>
      )
    },
  },
  [ColumnId.BidPrice]: {
    id: ColumnId.BidPrice,
    align: TableAlign.Right,
    name: 'Bid',
    renderColumn: (args) => {
      if (args.kind === RowKind.OptionRow) return null
      if (args.row.bidPrice === 0) return NULL_VALUE
      return formatNumber(args.row.bidPrice, FORMAT_SETTINGS)
    },
  },
  [ColumnId.AskPrice]: {
    id: ColumnId.AskPrice,
    align: TableAlign.Right,
    name: 'Ask',
    renderColumn: (args) => {
      if (args.kind === RowKind.OptionRow) return null
      if (args.row.askPrice === 0) return NULL_VALUE
      return formatNumber(args.row.askPrice, FORMAT_SETTINGS)
    },
  },
  [ColumnId.Volume]: {
    id: ColumnId.Volume,
    align: TableAlign.Right,
    name: 'Volume',
    renderColumn: (args) => {
      if (args.kind === RowKind.OptionRow) return null
      return formatNumber(args.row.lastVolume, { ...FORMAT_SETTINGS, fractions: 0 })
    },
  },
  [ColumnId.OpenInterest]: {
    id: ColumnId.OpenInterest,
    align: TableAlign.Right,
    name: 'Open Int.',
    renderColumn: (args) => {
      if (args.kind === RowKind.OptionRow) return null
      return formatNumber(args.row.openInterest, { ...FORMAT_SETTINGS, fractions: 0 })
    },
  },
  [ColumnId.StrikePrice]: {
    id: ColumnId.StrikePrice,
    align: TableAlign.Right,
    name: 'Strike',
    className: 'w-28',
    renderColumn: (args) => formatNumber(args.row.strike, FORMAT_SETTINGS),
  },
  [ColumnId.ExpirationDate]: {
    id: ColumnId.ExpirationDate,
    align: TableAlign.Right,
    name: 'Expiry',
    className: 'w-28',
    renderColumn: (args) => formatDate(args.row.exDate, DATE_FORMAT_INT, DATE_FORMAT_VIEW),
  },
  [ColumnId.ContractName]: {
    id: ColumnId.ContractName,
    align: TableAlign.Left,
    name: 'Contract Name',
    className: 'w-44',
    getValueForSort: (args) => `${args.ticker}${args.row.exDate}${args.row.strike}`,
    renderColumn: (args) => {
      if (!args.row) return NULL_VALUE
      const strike = (args.row.strike * 1000).toString().padStart(8, '0')

      return (
        <span className="text-gray-500 dark:text-gray-200">
          {args.ticker?.replace('-', '')}
          {args.row.exDate}
          {args.kind === RowKind.Call ? 'C' : 'P'}
          {strike}
        </span>
      )
    },
  },
  [ColumnId.LastTrade]: {
    id: ColumnId.LastTrade,
    align: TableAlign.Right,
    name: 'Last Trade',
    className: 'w-44',
    getValueForSort: (args) => {
      if (args.kind === RowKind.OptionRow) return null
      return new Date(args.row.lastTime)
    },
    renderColumn: (args) => {
      if (args.kind === RowKind.OptionRow) return null
      if (args.row.lastClose === 0) return NULL_VALUE
      return formatDate(new Date(args.row.lastTime), DATE_FORMAT_VIEW_LONG)
    },
  },
  [ColumnId.IV]: {
    id: ColumnId.IV,
    align: TableAlign.Right,
    name: 'IV',
    renderColumn: (args) => {
      if (args.kind === RowKind.OptionRow) return null
      if (!args.row.iv) return NULL_VALUE
      return formatNumber(args.row.iv * 100, FORMAT_SETTINGS) + '%'
    },
  },
  [ColumnId.Delta]: {
    id: ColumnId.Delta,
    align: TableAlign.Right,
    name: 'Delta',
    renderColumn: (args) => {
      if (args.kind === RowKind.OptionRow) return null
      if (!args.row.delta) return NULL_VALUE
      return formatNumber(args.row.delta, GREEK_SETTINGS)
    },
  },
  [ColumnId.Gamma]: {
    id: ColumnId.Gamma,
    align: TableAlign.Right,
    name: 'Gamma',
    renderColumn: (args) => {
      if (args.kind === RowKind.OptionRow) return null
      if (!args.row.gamma) return NULL_VALUE
      return formatNumber(args.row.gamma, GREEK_SETTINGS)
    },
  },
  [ColumnId.Theta]: {
    id: ColumnId.Theta,
    align: TableAlign.Right,
    name: 'Theta',
    renderColumn: (args) => {
      if (args.kind === RowKind.OptionRow) return null
      if (!args.row.theta) return NULL_VALUE
      return formatNumber(args.row.theta, GREEK_SETTINGS)
    },
  },
  [ColumnId.Vega]: {
    id: ColumnId.Vega,
    align: TableAlign.Right,
    name: 'Vega',
    renderColumn: (args) => {
      if (args.kind === RowKind.OptionRow) return null
      if (!args.row.vega) return NULL_VALUE
      return formatNumber(args.row.vega, GREEK_SETTINGS)
    },
  },
  [ColumnId.Rho]: {
    id: ColumnId.Rho,
    align: TableAlign.Right,
    name: 'Rho',
    renderColumn: (args) => {
      if (args.kind === RowKind.OptionRow) return null
      if (!args.row.rho) return NULL_VALUE
      return formatNumber(args.row.rho, GREEK_SETTINGS)
    },
  },
})
